import React, { useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { VoicesConfig } from "../../../Models/voicesConfig";
import {
  useRemoveVoicesBufferKey,
  useSetVoicesBufferKey,
} from "../../../Query/useVoicesConfig";
import { ComoButton } from "../../Common/ComoButton";
import VoicesCard from "../Common/VoicesCard";
import VoicesField from "../Common/VoicesField";
import VoicesInput from "../Common/VoicesInput";

interface Props {
  config: VoicesConfig;
  canManage: boolean;
}

const VoicesBufferConnection: React.FC<Props> = ({ config, canManage }) => {
  const { t } = useTranslation("voicesSettings");
  const setKey = useSetVoicesBufferKey();
  const removeKey = useRemoveVoicesBufferKey();
  const [apiKey, setApiKey] = useState("");

  const save = () =>
    setKey.mutate(apiKey.trim(), { onSuccess: () => setApiKey("") });

  return (
    <VoicesCard title={t("bufferTitle")} subtitle={t("bufferSubtitle")}>
      <Stack spacing={2.5}>
        <Typography variant="body2" sx={{ color: color.gray700 }}>
          {config.buffer_connected
            ? t("bufferConnectedWith", { hint: config.buffer_key_hint })
            : t("bufferNotConnected")}
        </Typography>

        {canManage && (
          <>
            <VoicesField
              label={
                config.buffer_connected ? t("bufferReplaceKey") : t("bufferKey")
              }
              hint={t("bufferKeyHint")}
            >
              <VoicesInput
                type="password"
                autoComplete="off"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
            </VoicesField>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              {config.buffer_connected && (
                <Button
                  onClick={() => removeKey.mutate()}
                  disabled={removeKey.isPending}
                  sx={{ color: color.gray600, textTransform: "none" }}
                >
                  {t("bufferDisconnect")}
                </Button>
              )}
              <ComoButton
                onClick={save}
                disabled={!apiKey.trim() || setKey.isPending}
              >
                {setKey.isPending ? t("bufferChecking") : t("bufferSave")}
              </ComoButton>
            </Box>
          </>
        )}
      </Stack>
    </VoicesCard>
  );
};

export default VoicesBufferConnection;
